import {
  ISSUE_MGMT_WRITE_PERMISSION,
  type IssueReviewWeekday,
  type IssueSettingChange,
} from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ChoiceChips } from '../components/issue-mgmt/ChoiceChips.js';
import {
  IngestChannelsSetting,
  InspectionSetting,
  JobsSetting,
} from '../components/issue-mgmt/IngestSettings.js';
import { ToggleSwitch } from '../components/issue-mgmt/ToggleSwitch.js';
import { useIssueToast } from '../components/issue-mgmt/useIssueToast.js';
import { useIssueMembers, useIssueMgmtWrite, useIssueSettings } from '../hooks/useIssueMgmt.js';
import { usePermissions } from '../hooks/usePermissions.js';
import { ISSUE_WEEKDAY_LABEL_KEYS } from '../lib/issue-mgmt-format.js';

/**
 * 週次の曜日の並び。
 * @graph-connects none
 */
const WEEKDAYS: readonly IssueReviewWeekday[] = ['mon', 'tue', 'wed', 'thu', 'fri'];

/**
 * 設定の1行（左に名前と1行の説明、右に値と操作）。
 * @graph-connects none
 */
function SettingRow({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-3 border-b border-[var(--color-border)] px-5 py-4 last:border-b-0 md:grid-cols-[14rem_1fr] md:gap-6">
      <div>
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</h2>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">{description}</p>
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/**
 * 課題管理の「設定」タブ（SC-05）。振り分けの当番・判定者・週次の曜日・取込みチャンネル・AIのおすすめ・定期処理・
 * 月1回の点検を並べる。
 *
 * 当番と判定者は編集キーを持つ人の札から1人選び、押すとすぐ保存する。閲覧キーだけの人は見るだけで、札や操作は押せない。
 *
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の当番・判定者・週次の曜日・取込みチャンネル・AIのおすすめ・定期処理・月1回の点検を、編集キーの人がその場で変える設定画面
 * @graph-connects useIssueSettings [calls] 設定
 * @graph-connects useIssueMembers [calls] 当番・判定者の候補
 * @graph-connects useIssueMgmtWrite [calls] updateSettings
 * @graph-connects usePermissions [calls] 編集キーの有無
 * @graph-connects ToggleSwitch [calls] AIのおすすめの切り替え
 * @graph-connects IngestChannelsSetting [calls] 取込みチャンネル
 * @graph-connects JobsSetting [calls] 定期処理
 * @graph-connects InspectionSetting [calls] 月1回の点検
 */
export function IssueMgmtSettingsPage({ now = new Date() }: { now?: Date }) {
  const { t } = useTranslation();
  const settings = useIssueSettings();
  const members = useIssueMembers();
  const update = useIssueMgmtWrite('updateSettings');
  const toast = useIssueToast();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canEdit = !permissionsLoading && hasPermission(ISSUE_MGMT_WRITE_PERMISSION);

  if (settings.isError) {
    return (
      <div className="space-y-2 rounded-xl border border-[var(--color-border)] p-6 text-center">
        <p className="text-sm text-[var(--color-error)]">{t('issueMgmt.common.loadError')}</p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void settings.refetch();
          }}
        >
          {t('issueMgmt.common.retry')}
        </Button>
      </div>
    );
  }
  if (settings.data === undefined) {
    return (
      <p className="py-10 text-center text-sm text-[var(--color-text-muted)]">
        {t('issueMgmt.common.loading')}
      </p>
    );
  }

  const current = settings.data;
  const memberChoices = (members.data?.members ?? []).map((m) => ({
    value: m.email,
    label: m.displayName,
  }));
  const saveChange = (change: IssueSettingChange, message: string) => {
    update.mutate(
      { changes: [change] },
      {
        onSuccess: () => {
          toast.notifySaved(message);
        },
        onError: toast.notifyError,
      },
    );
  };
  const personPicker = (field: 'triageOwner' | 'judge', label: string) => (
    <>
      {memberChoices.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">
          {t('issueMgmt.settings.noMembers')}
        </p>
      ) : (
        <ChoiceChips<string>
          label={label}
          value={current[field]}
          disabled={!canEdit || update.isPending}
          onChange={(value) => {
            saveChange({ field, base: current[field], value }, t('issueMgmt.settings.saved'));
          }}
          choices={memberChoices}
        />
      )}
      {current[field] === null && (
        <p className="mt-1 text-xs text-[var(--color-warning)]">{t('issueMgmt.settings.notSet')}</p>
      )}
    </>
  );

  return (
    <div className="max-w-4xl space-y-3">
      {!canEdit && !permissionsLoading && (
        <p className="text-xs text-[var(--color-text-muted)]">{t('issueMgmt.settings.readOnly')}</p>
      )}
      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        <SettingRow
          title={t('issueMgmt.settings.triageOwner')}
          description={t('issueMgmt.settings.triageOwnerDesc')}
        >
          {personPicker('triageOwner', t('issueMgmt.settings.triageOwner'))}
        </SettingRow>
        <SettingRow
          title={t('issueMgmt.settings.judge')}
          description={t('issueMgmt.settings.judgeDesc')}
        >
          {personPicker('judge', t('issueMgmt.settings.judge'))}
        </SettingRow>
        <SettingRow
          title={t('issueMgmt.settings.reviewWeekday')}
          description={t('issueMgmt.settings.reviewWeekdayDesc')}
        >
          <ChoiceChips<IssueReviewWeekday>
            label={t('issueMgmt.settings.reviewWeekday')}
            value={current.reviewWeekday}
            disabled={!canEdit || update.isPending}
            onChange={(value) => {
              saveChange(
                { field: 'reviewWeekday', base: current.reviewWeekday, value },
                t('issueMgmt.settings.saved'),
              );
            }}
            choices={WEEKDAYS.map((value) => ({
              value,
              label: t(ISSUE_WEEKDAY_LABEL_KEYS[value]),
            }))}
          />
        </SettingRow>
        <SettingRow
          title={t('issueMgmt.settings.channels')}
          description={t('issueMgmt.settings.channelsDesc')}
        >
          <IngestChannelsSetting canEdit={canEdit} now={now} />
        </SettingRow>
        <SettingRow
          title={t('issueMgmt.settings.aiSuggestions')}
          description={t('issueMgmt.settings.aiSuggestionsDesc')}
        >
          <ToggleSwitch
            checked={current.aiSuggestions}
            label={t('issueMgmt.settings.aiSuggestions')}
            disabled={!canEdit || update.isPending}
            onToggle={() => {
              const next = !current.aiSuggestions;
              saveChange(
                { field: 'aiSuggestions', base: current.aiSuggestions, value: next },
                next ? t('issueMgmt.settings.aiOn') : t('issueMgmt.settings.aiOff'),
              );
            }}
          >
            {current.aiSuggestions ? t('issueMgmt.settings.on') : t('issueMgmt.settings.off')}
          </ToggleSwitch>
        </SettingRow>
        <SettingRow
          title={t('issueMgmt.settings.jobs')}
          description={t('issueMgmt.settings.jobsDesc')}
        >
          <JobsSetting canEdit={canEdit} now={now} />
        </SettingRow>
        <SettingRow
          title={t('issueMgmt.settings.inspection')}
          description={t('issueMgmt.settings.inspectionDesc')}
        >
          <InspectionSetting canEdit={canEdit} now={now} />
        </SettingRow>
      </section>
    </div>
  );
}
